import {useItemPickersContext} from '@github-ui/item-picker'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {Box, Heading, IconButton, Overlay} from '@primer/react'
import {Suspense, useEffect, useRef, useCallback, useState, useMemo} from 'react'
import {type PreloadedQuery, useQueryLoader, graphql, usePreloadedQuery} from 'react-relay'
import {useFragment, useRelayEnvironment, type Environment, fetchQuery} from 'react-relay/hooks'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {noop} from '@github-ui/noop'
import type {MarkdownComposerRef} from '@github-ui/commenting'
import {IssueCommentComposer, CommentingConstants, useCommentEditsContext} from '@github-ui/commenting'
import {IssueBodyInternal} from '@github-ui/issue-body'

import {CLASS_NAMES, IDS, TEST_IDS} from '../constants'
import {useInputElementActiveContext} from '../contexts'
import type {ItemIdentifier} from '../types/issue'
import {Header} from './header/Header'
import {IssueTimelineInternal} from './IssueTimeline'
import {ScrollToTop} from './ScrollToTop'
import {renderIssueViewerErrors} from './shared/IssueViewerError'
import type {IssueViewerViewQuery} from './__generated__/IssueViewerViewQuery.graphql'
import {IssueViewerLoading} from './IssueViewerLoading'
import type {IssueViewerViewer$key} from './__generated__/IssueViewerViewer.graphql'
import type {IssueViewerView$key} from './__generated__/IssueViewerView.graphql'
import {ContentWrapper} from './ContentWrapper'
import {InfoIcon, XIcon} from '@primer/octicons-react'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from './OptionConfig'
import {ISSUE_EVENTS, LABELS, getHightlightedEventText} from '@github-ui/timeline-items'
import {useBeforeUnload} from 'react-router-dom'
import {useHash} from '../hooks/use-hash'
import {createOperationDescriptor, getRequest, type FetchQueryFetchPolicy} from 'relay-runtime'
import {IssueSidebar} from './IssueSidebar'

export type IssueViewerQueries = {
  issueViewerViewQuery: IssueViewerViewQuery
}

type IssueViewerProps = {
  itemIdentifier: ItemIdentifier
  optionConfig?: OptionConfig
}

type IssueViewerInternalProps = {
  optionConfig: OptionConfig
  issueViewerViewRef: PreloadedQuery<IssueViewerViewQuery>
  containerRef: React.RefObject<HTMLDivElement>
}

type IssueViewerInternalFragmentProps = {
  optionConfig: OptionConfig
  viewerFragment: IssueViewerViewer$key
  issueFragment: IssueViewerView$key
  containerRef: React.RefObject<HTMLDivElement>
}

type fetchSecondaryIssuesQueryProps = {
  environment: Environment
  itemIdentifier: ItemIdentifier
  fetchPolicy: FetchQueryFetchPolicy
}

export const IssueViewerViewGraphqlQuery = graphql`
  query IssueViewerViewQuery($repo: String!, $owner: String!, $number: Int!, $allowedOwner: String) {
    repository(name: $repo, owner: $owner) {
      issue(number: $number) @required(action: THROW) {
        ...IssueViewerView @arguments(allowedOwner: $allowedOwner)
      }
    }
    viewer {
      ...IssueViewerViewer
    }
  }
`

const IssueViewerSecondaryGraphqlQuery = graphql`
  query IssueViewerSecondaryViewQuery($repo: String!, $owner: String!, $number: Int!) {
    repository(name: $repo, owner: $owner) {
      issue(number: $number) {
        ...IssueSidebarSecondaryQuery
      }
      ...LazyContributorFooter
    }
  }
`

export function fetchSecondaryIssuesQuery({environment, itemIdentifier, fetchPolicy}: fetchSecondaryIssuesQueryProps) {
  const queryRequest = getRequest(IssueViewerSecondaryGraphqlQuery)
  const variables = {
    repo: itemIdentifier.repo,
    owner: itemIdentifier.owner,
    number: itemIdentifier.number,
  }
  const queryDescriptor = createOperationDescriptor(queryRequest, variables)
  environment.retain(queryDescriptor)
  fetchQuery(environment, IssueViewerSecondaryGraphqlQuery, variables, {fetchPolicy}).subscribe({})
}

const issueViewerViewFragment = graphql`
  fragment IssueViewerView on Issue @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    id
    # eslint-disable-next-line relay/unused-fields the updatedAt field is used for an experiment to count the different version of an issue
    updatedAt
    ...Header
    ...IssueBody
    ...IssueCommentComposer
    ...IssueActions
    ...IssueSidebarPrimaryQuery @arguments(allowedOwner: $allowedOwner)
    ...IssueTimelinePaginated @arguments(numberOfTimelineItems: 15)
  }
`

export function IssueViewer({itemIdentifier, optionConfig = ISSUE_VIEWER_DEFAULT_CONFIG}: IssueViewerProps) {
  const {repo, owner, number} = itemIdentifier
  const environment = useRelayEnvironment()

  const [issueViewerViewRef, loadIssueViewerView] = useQueryLoader<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
  )
  const issueViewerContainerRef = useRef<HTMLDivElement>(null)

  const fetchPolicy = optionConfig.issueQueriesFetchingPolicy?.fetchPolicy
  useEffect(() => {
    if (!optionConfig.preloadedQueries?.issueViewerViewQuery) {
      loadIssueViewerView({owner, repo, number, allowedOwner: optionConfig.allowedProjectOwner ?? null}, {fetchPolicy})
    }
  }, [
    loadIssueViewerView,
    owner,
    repo,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    optionConfig.allowedProjectOwner,
    fetchPolicy,
    number,
  ])

  useEffect(() => {
    fetchSecondaryIssuesQuery({
      environment,
      itemIdentifier,
      fetchPolicy: 'store-or-network',
    })
  }, [environment, itemIdentifier])

  if (!issueViewerViewRef) return <IssueViewerLoading />

  return (
    <PreloadedQueryBoundary
      key={`${owner}-${repo}-${number}`}
      onRetry={() =>
        loadIssueViewerView(
          {owner, repo, number, allowedOwner: optionConfig.allowedProjectOwner ?? null},
          {fetchPolicy: 'network-only'},
        )
      }
      fallback={renderIssueViewerErrors}
    >
      <Box
        ref={issueViewerContainerRef}
        sx={{
          display: 'flex',
          flex: 'auto',
          flexDirection: 'column',
          justifyContent: 'stretch',
          alignItems: 'center',
          position: 'relative',
          pt: 3,
        }}
      >
        <Suspense fallback={<IssueViewerLoading />}>
          <IssueViewerInternal
            issueViewerViewRef={issueViewerViewRef}
            containerRef={issueViewerContainerRef}
            optionConfig={optionConfig}
          />
        </Suspense>
      </Box>
    </PreloadedQueryBoundary>
  )
}

function IssueViewerInternal({issueViewerViewRef, containerRef, optionConfig}: IssueViewerInternalProps) {
  const {repository, viewer} = usePreloadedQuery<IssueViewerViewQuery>(IssueViewerViewGraphqlQuery, issueViewerViewRef)

  return viewer && repository?.issue ? (
    <IssueViewerInternalFragment
      viewerFragment={viewer}
      issueFragment={repository.issue}
      containerRef={containerRef}
      optionConfig={optionConfig}
    />
  ) : null
}

export function IssueViewerInternalFragment({
  viewerFragment,
  issueFragment,
  optionConfig,
  containerRef,
}: IssueViewerInternalFragmentProps) {
  const issue = useFragment(issueViewerViewFragment, issueFragment)
  const viewer = useFragment(
    graphql`
      fragment IssueViewerViewer on User {
        ...TimelineItemsViewer
        ...AssigneePickerAssignee
        ...ReactionViewerUser
      }
    `,
    viewerFragment,
  )

  const composerRef = useRef<MarkdownComposerRef>(null)
  const {startCommentEdit, cancelCommentEdit, isCommentEditActive} = useCommentEditsContext()
  const issueBodyKey = `issue-${issue.id}-body`
  const {setInputElementState, clearInputElementStates} = useInputElementActiveContext()
  const {anyItemPickerOpen} = useItemPickersContext()
  const breakpoint = useContainerBreakpoint(containerRef.current)

  // Reset comment and input states on issue change
  useEffect(() => {
    clearInputElementStates()
  }, [issue.id, clearInputElementStates])

  useEffect(() => {
    setInputElementState(IDS.itemPicker, anyItemPickerOpen())
  }, [anyItemPickerOpen, setInputElementState])

  // Whenever the issue body editing state changes, synchronize that state via callbacks
  const onIssueEditStateChange = optionConfig.onIssueEditStateChange
  useEffect(() => {
    onIssueEditStateChange?.(isCommentEditActive())
  }, [isCommentEditActive, onIssueEditStateChange])

  const handleCommentReply = useCallback((quotedComment: string) => {
    composerRef.current?.setText(quotedComment)
    setTimeout(() => composerRef.current?.focus(), 0)
  }, [])

  const onCommentChange = useCallback(
    (id: string) => {
      startCommentEdit(CommentingConstants.VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditStart?.(id)
    },
    [issue.id, optionConfig, startCommentEdit],
  )
  const onCommentEditCancel = useCallback(
    (id: string) => {
      cancelCommentEdit(CommentingConstants.VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditCancel?.(id)
    },
    [cancelCommentEdit, issue.id, optionConfig],
  )

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isCommentEditActive()) {
        event.preventDefault()
        return (event.returnValue = '')
      }
    },
    [isCommentEditActive],
  )

  useBeforeUnload(beforeUnload)

  const [detailPaneOpen, setDetailPaneOpen] = useState(false)
  const exitOverlay = useCallback(() => setDetailPaneOpen(false), [])

  const isNarrow = useMemo(() => breakpoint([true, true, false, false]), [breakpoint])

  const metadataContent = <IssueSidebar sidebarKey={issue} viewer={viewer} optionConfig={optionConfig} />

  const MetadataPaneTrigger = useCallback(
    () => (
      <>
        {isNarrow && optionConfig.responsiveRightSidepanel && (
          <IconButton
            aria-label="Show metadata pane"
            icon={InfoIcon}
            variant="invisible"
            onClick={() => setDetailPaneOpen(true)}
            ref={toggleSidesheetRef}
          />
        )}
      </>
    ),
    [isNarrow, optionConfig.responsiveRightSidepanel],
  )

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const toggleSidesheetRef = useRef<HTMLButtonElement>(null)
  const renderRightOverlay = useMemo(
    () => optionConfig.responsiveRightSidepanel && isNarrow && detailPaneOpen,
    [detailPaneOpen, isNarrow, optionConfig.responsiveRightSidepanel],
  )
  const renderMetadataPane = useMemo(
    () => !isNarrow || (isNarrow && !optionConfig.responsiveRightSidepanel),
    [isNarrow, optionConfig.responsiveRightSidepanel],
  )
  const handleBodyEditStateChange = useCallback(
    (isEditing: boolean) => {
      optionConfig?.onIssueEditStateChange?.(isEditing)
      if (isEditing) {
        startCommentEdit(issueBodyKey)
      } else {
        cancelCommentEdit(issueBodyKey)
      }
    },
    [cancelCommentEdit, issueBodyKey, optionConfig, startCommentEdit],
  )
  // This is used to subscribe to hash-change events
  // Specifically, it is used to detect when the user has (soft) navigated to a comment in the same issue
  useHash()

  return (
    <>
      <Header issue={issue} metadataPaneTrigger={<MetadataPaneTrigger />} optionConfig={optionConfig} />
      <ContentWrapper sx={optionConfig?.innerSx}>
        <Box
          sx={{
            display: 'flex',
            flex: 'auto',
            flexDirection: breakpoint(['column', 'column', 'row', 'row']),
            justifyContent: 'stretch',
            gap: [2, 2, 2, 4],
          }}
        >
          <Box
            sx={{
              width: breakpoint(['100%', '100%', '70%', '75%']),
              backgroundColor: 'canvas.default',
              zIndex: 1,
              flexGrow: 1,
            }}
          >
            <Box data-testid={TEST_IDS.issueViewerIssueContainer}>
              <IssueBodyInternal
                issue={issue}
                viewer={viewer}
                onLinkClick={optionConfig.onLinkClick}
                commentBoxConfig={optionConfig.commentBoxConfig}
                onIssueEditStateChange={handleBodyEditStateChange}
                onIssueUpdate={optionConfig.onIssueUpdate}
                isIssueEditActive={isCommentEditActive}
                onCommentReply={handleCommentReply}
                highlightedEventText={ssrSafeLocation.hash}
              />
            </Box>
            <Box data-testid={TEST_IDS.issueViewerCommentsContainer} className={CLASS_NAMES.commentsContainer}>
              <Box
                sx={{
                  mb: 3,
                }}
              >
                <ScrollToTop />
                <IssueTimelineInternal
                  issue={issue}
                  highlightedEventText={getHightlightedEventText(ssrSafeLocation.hash, ISSUE_EVENTS)}
                  viewer={viewer}
                  onLinkClick={optionConfig.onLinkClick}
                  onCommentReply={handleCommentReply}
                  onCommentChange={onCommentChange}
                  onCommentEditCancel={onCommentEditCancel}
                  commentBoxConfig={optionConfig.commentBoxConfig}
                  timelineEventBaseUrl={optionConfig.timelineEventBaseUrl}
                  withLiveUpdates={optionConfig.withLiveUpdates}
                  withSecondaryFetch={optionConfig.withSecondaryFetch}
                  navigate={optionConfig.navigate ?? noop}
                />
                <IssueCommentComposer
                  ref={composerRef}
                  issue={issue}
                  onChange={() => {
                    startCommentEdit(CommentingConstants.VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                    optionConfig.onCommentEditStart?.(IDS.newComment)
                  }}
                  onSave={() => {
                    cancelCommentEdit(CommentingConstants.VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                    optionConfig.onCommentEditCancel?.(IDS.newComment)
                  }}
                  onCancel={() => {
                    cancelCommentEdit(CommentingConstants.VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
                    optionConfig.onCommentEditCancel?.(IDS.newComment)
                  }}
                  onNewIssueComment={optionConfig.onNewIssueComment}
                  commentBoxConfig={optionConfig.commentBoxConfig}
                  singleKeyShortcutEnabled={optionConfig.singleKeyShortcutsEnabled || false}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flexBasis: breakpoint(['auto', 'auto', '256px', '296px']),
              flexShrink: 0,
              overflow: 'hidden',
            }}
            data-testid={TEST_IDS.issueViewerMetadataContainer}
          >
            {renderRightOverlay ? (
              <Overlay
                anchorSide="inside-left"
                aria-label="Details"
                initialFocusRef={closeButtonRef}
                position="fixed"
                returnFocusRef={toggleSidesheetRef}
                right={0}
                top={0}
                sx={{
                  p: 3,
                  width: '440px',
                  height: '100vh',
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
                onClickOutside={exitOverlay}
                onEscape={exitOverlay}
              >
                <Box sx={{maxHeight: '100%', overflowY: 'scroll'}}>
                  <Box
                    sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2, pl: 2}}
                  >
                    <Heading as="h2" sx={{fontSize: 2, fontWeight: 600}}>
                      {LABELS.metadataHeader}
                    </Heading>
                    <IconButton
                      ref={closeButtonRef}
                      aria-label="Close overview"
                      icon={XIcon}
                      variant="invisible"
                      onClick={exitOverlay}
                    />
                  </Box>
                  {metadataContent}
                </Box>
              </Overlay>
            ) : (
              <> </>
            )}
            {renderMetadataPane && (
              <>
                <Heading as="h2" className="sr-only" sx={{fontSize: 2, ml: 3, mb: 2}}>
                  {LABELS.metadataHeader}
                </Heading>
                {metadataContent}
              </>
            )}
          </Box>
        </Box>
      </ContentWrapper>
    </>
  )
}

try{ IssueViewer.displayName ||= 'IssueViewer' } catch {}
try{ IssueViewerInternal.displayName ||= 'IssueViewerInternal' } catch {}
try{ IssueViewerInternalFragment.displayName ||= 'IssueViewerInternalFragment' } catch {}
try{ MetadataPaneTrigger.displayName ||= 'MetadataPaneTrigger' } catch {}